import React, { useEffect, useState } from 'react';
import { MdAdd, MdDelete, MdNotificationsActive, MdPhone, MdPerson, MdGroup, MdEmail, MdEdit, MdSave, MdClose } from 'react-icons/md';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';

const TABLE_OPTIONS = ['AP', ...Array.from({ length: 10 }, (_, i) => `${i + 1}`)];

const SMSNotification = () => {
  const [form, setForm] = useState({
    fullName: '',
    tableNumbers: [],
    contact: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const mapApiToRecipient = (item) => ({
    id: item.id,
    fullName: item.full_name || item.fullName || '',
    tableNumbers: typeof item.table_numbers === 'string'
      ? item.table_numbers.split(',').filter(Boolean)
      : Array.isArray(item.table_numbers) ? item.table_numbers : [],
    contact: item.contact || '',
    email: item.email || '',
  });

  const fetchLeaders = async () => {
    setIsLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }
      const resp = await fetch('https://usibras-web-backend.onrender.com/api/manual-peeling-table-leaders?page=1', {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });
      const result = await resp.json();
      if (!resp.ok) {
        throw new Error(result.message || result.error || 'Failed to load table leaders');
      }
      const data = Array.isArray(result.data) ? result.data : [];
      setRecipients(data.map(mapApiToRecipient));
    } catch (err) {
      showToast(err.message || 'Failed to load table leaders', 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaders();
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 2500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleTableChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setForm((prev) => ({ ...prev, tableNumbers: selected }));
    if (errors.tableNumbers) setErrors((prev) => ({ ...prev, tableNumbers: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.fullName.trim()) nextErrors.fullName = 'Full name is required';
    if (!form.contact.trim()) nextErrors.contact = 'Contact is required';
    // Basic phone validation similar to other screens
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (form.contact && !phoneRegex.test(form.contact)) {
      nextErrors.contact = 'Enter a valid phone number';
    }
    if (!form.tableNumbers || form.tableNumbers.length === 0) {
      nextErrors.tableNumbers = 'Select at least one table';
    }
    // Email validation - required
    if (!form.email.trim()) {
      nextErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        nextErrors.email = 'Enter a valid email address';
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleEdit = (recipient) => {
    setEditingId(recipient.id);
    setForm({
      fullName: recipient.fullName || '',
      tableNumbers: recipient.tableNumbers || [],
      contact: recipient.contact || '',
      email: recipient.email || '',
    });
    setErrors({});
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ fullName: '', tableNumbers: [], contact: '', email: '' });
    setErrors({});
  };

  const handleAddRecipient = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast('Please fix the errors', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const payload = {
        full_name: form.fullName.trim(),
        table_numbers: (form.tableNumbers || []).join(','),
        contact: form.contact.trim(),
        email: form.email.trim(),
      };

      let resp;
      let result;

      if (editingId) {
        // Update existing recipient
        resp = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-table-leaders/${editingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        result = await resp.json();

        if (!resp.ok) {
          throw new Error(result.message || result.error || 'Failed to update table leader');
        }

        // Update local state
        setRecipients((prev) =>
          prev.map((r) =>
            r.id === editingId
              ? {
                  id: r.id,
                  fullName: payload.full_name,
                  tableNumbers: (payload.table_numbers || '').split(',').filter(Boolean),
                  contact: payload.contact,
                  email: payload.email || '',
                }
              : r
          )
        );
        showToast(result.message || 'Table leader updated successfully', 'success');
        handleCancelEdit();
      } else {
        // Add new recipient
        resp = await fetch('https://usibras-web-backend.onrender.com/api/manual-peeling-table-leaders', {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        result = await resp.json();

        if (!resp.ok) {
          throw new Error(result.message || result.error || 'Failed to add table leader');
        }

        const entry = {
          id: result.id ?? `${Date.now()}`,
          fullName: payload.full_name,
          tableNumbers: (payload.table_numbers || '').split(',').filter(Boolean),
          contact: payload.contact,
          email: payload.email || '',
        };
        setRecipients((prev) => [entry, ...prev]);
        setForm({ fullName: '', tableNumbers: [], contact: '', email: '' });
        showToast(result.message || 'Table leader added successfully', 'success');
      }
      // Optionally refresh list from server to ensure consistency
      fetchLeaders();
    } catch (err) {
      showToast(err.message || (editingId ? 'Failed to update table leader' : 'Failed to add table leader'), 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipient?')) {
      return;
    }
    
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const resp = await fetch(`https://usibras-web-backend.onrender.com/api/manual-peeling-table-leaders/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      const result = await resp.json();

      if (!resp.ok) {
        throw new Error(result.message || result.error || 'Failed to delete table leader');
      }

      // If we're editing this item, cancel edit mode
      if (editingId === id) {
        handleCancelEdit();
      }

      setRecipients((prev) => prev.filter((r) => r.id !== id));
      showToast(result.message || 'Recipient deleted successfully', 'success');
      // Refresh list from server to ensure consistency
      fetchLeaders();
    } catch (err) {
      showToast(err.message || 'Failed to delete recipient', 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <MdNotificationsActive className="mr-3 text-green-600" />
            SMS Notification Recipients
          </h1>
          <button
            onClick={fetchLeaders}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-600">
          Add staff who should receive SMS notifications.
        </p>
      </div>

      <div className={`bg-white rounded-2xl shadow-lg border p-6 mb-8 ${editingId ? 'border-blue-300 border-2' : 'border-gray-100'}`}>
        {editingId && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 flex items-center">
              <MdEdit className="mr-2" />
              <span className="font-medium">Editing recipient</span>
            </p>
          </div>
        )}
        <form onSubmit={handleAddRecipient} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="e.g., Ama Serwaa"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Table Number(s) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdGroup className="absolute left-3 top-3 text-gray-400" />
              <select
                multiple
                value={form.tableNumbers}
                onChange={handleTableChange}
                className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors min-h-[52px] ${
                  errors.tableNumbers ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {TABLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {errors.tableNumbers && <p className="text-red-500 text-sm mt-1">{errors.tableNumbers}</p>}
            {form.tableNumbers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">Selected: {form.tableNumbers.join(', ')}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                name="contact"
                value={form.contact}
                onChange={handleChange}
                placeholder="eg., 0543370183"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
                  errors.contact ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.contact && <p className="text-red-500 text-sm mt-1">{errors.contact}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="eg., name@example.com"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div className="md:col-span-2 lg:col-span-4 flex justify-end gap-3">
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <MdClose className="mr-2" />
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-3 text-white rounded-lg transition-colors flex items-center disabled:opacity-60 disabled:cursor-not-allowed ${
                editingId
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {editingId ? (
                <>
                  <MdSave className="mr-2" />
                  {isSubmitting ? 'Updating...' : 'Update Recipient'}
                </>
              ) : (
                <>
                  <MdAdd className="mr-2" />
                  {isSubmitting ? 'Saving...' : 'Add Recipient'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-green-500 to-green-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Full Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Table Number(s)</th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : recipients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                    No recipients yet. Add the first one above.
                  </td>
                </tr>
              ) : (
                recipients.map((r) => (
                  <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${editingId === r.id ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{r.fullName}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full">
                        {r.tableNumbers.join(', ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.contact}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.email || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(r)}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center"
                          title="Edit"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                          title="Remove"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default SMSNotification;


